import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MoneyInputDirective } from '../../utils/directives/money-input.directive';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { AccountCreditService } from '../../domain/services/account-credit/account-credit.service';
import { AccountCredit } from '../../domain/models/account-credit.model';

/**
 * Reusable panel showing an account's credit balance + ledger history, with an
 * apply-to-return action. Drop onto an account/return detail view with [accountId].
 */
@Component({
  selector: 'app-account-credit-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCardModule, MatButtonModule, MatIconModule, MatTableModule, MoneyInputDirective],
  template: `
    <mat-card class="credit" *ngIf="accountId">
      <h3 class="credit__title"><mat-icon>savings</mat-icon> Account credit</h3>
      <div class="credit__balance">Available balance: <strong>{{ balance | currency }}</strong></div>

      <!-- With no balance the panel was a bare number with no way to tell what it was for. -->
      <p class="credit__hint" *ngIf="balance <= 0 && !history.length">
        Credit builds up when an account overpays — amending a return below what was already
        paid leaves the difference here, to put toward a later return.
      </p>

      <div class="credit__apply" *ngIf="balance > 0">
        <input type="number" [(ngModel)]="targetReturnId" placeholder="Return ID" class="credit__in">
        <input type="number" appMoney [(ngModel)]="applyAmount" step="0.01" placeholder="Amount" class="credit__in">
        <button mat-flat-button color="primary" [disabled]="busy || !targetReturnId || !applyAmount" (click)="apply()">Apply</button>
      </div>
      <div class="credit__err" *ngIf="error">{{ error }}</div>

      <table mat-table [dataSource]="history" class="credit__table" *ngIf="history.length">
        <ng-container matColumnDef="createdAt">
          <th mat-header-cell *matHeaderCellDef>Date</th>
          <td mat-cell *matCellDef="let e">{{ e.createdAt | date:'short' }}</td>
        </ng-container>
        <ng-container matColumnDef="eventType">
          <th mat-header-cell *matHeaderCellDef>Event</th>
          <td mat-cell *matCellDef="let e">{{ e.eventType }}</td>
        </ng-container>
        <ng-container matColumnDef="amount">
          <th mat-header-cell *matHeaderCellDef>Amount</th>
          <td mat-cell *matCellDef="let e">{{ e.amount | currency }}</td>
        </ng-container>
        <ng-container matColumnDef="balanceAfter">
          <th mat-header-cell *matHeaderCellDef>Balance</th>
          <td mat-cell *matCellDef="let e">{{ e.balanceAfter | currency }}</td>
        </ng-container>
        <ng-container matColumnDef="reason">
          <th mat-header-cell *matHeaderCellDef>Reason</th>
          <td mat-cell *matCellDef="let e">{{ e.reason }}</td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="cols"></tr>
        <tr mat-row *matRowDef="let row; columns: cols"></tr>
      </table>
    </mat-card>
  `,
  styles: [`
    .credit { padding: 16px; }
    .credit__hint { margin: 4px 0 0; font-size: 13px; color: #64748b; line-height: 1.5; max-width: 60ch; }
    .credit__title { display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; color: #1e293b; margin: 0 0 8px; }
    .credit__balance { font-size: 14px; margin-bottom: 12px; }
    .credit__apply { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; }
    .credit__in { width: 130px; padding: 6px 8px; border: 1px solid #cbd5e1; border-radius: 4px; }
    .credit__err { color: #b91c1c; font-size: 13px; margin-bottom: 8px; }
    .credit__table { width: 100%; }
  `]
})
export class AccountCreditPanelComponent implements OnChanges {
  @Input() accountId!: number;

  balance = 0;
  history: AccountCredit[] = [];
  targetReturnId: number | null = null;
  applyAmount: number | null = null;
  busy = false;
  error: string | null = null;

  readonly cols = ['createdAt', 'eventType', 'amount', 'balanceAfter', 'reason'];

  constructor(private readonly creditService: AccountCreditService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.accountId) { this.reload(); }
  }

  reload(): void {
    this.creditService.getBalance(this.accountId).subscribe({
      next: (b) => { this.balance = b.balance; },
      error: () => { /* non-blocking */ },
    });
    this.creditService.getHistory(this.accountId).subscribe({
      next: (h) => { this.history = h; },
      error: () => { /* non-blocking */ },
    });
  }

  apply(): void {
    if (!this.targetReturnId || !this.applyAmount) { return; }
    this.busy = true;
    this.error = null;
    this.creditService.apply(this.accountId, this.targetReturnId, this.applyAmount).subscribe({
      next: () => { this.busy = false; this.targetReturnId = null; this.applyAmount = null; this.reload(); },
      error: (e) => { this.busy = false; this.error = e?.error?.message || 'Apply failed.'; },
    });
  }
}
